import { useCallback } from "react";
import confirm from "@app/services/confirm/confirm";
import notification from "@app/services/notification";
import { storePlacesServices, type StorePlaceCancelPlacementParams } from "@src/businessModules/Stock/api/storePlaces";
import {
    buildStockPlacementCancelConfirmOptions,
    type BuildStockPlacementCancelConfirmParams,
} from "./buildStockPlacementCancelConfirmOptions";
import type { StockPlacementCancelPlacementVariables } from "./useStockPlacementCardMutations";

/** Параметры подтверждения отмены размещения. */
export interface ConfirmStockPlacementCancelParams extends StorePlaceCancelPlacementParams {
    /** Код строки состава ПО для точечного refetch после отмены. */
    contentCode: number;
    /** Поля контекста строки состава для confirm. */
    fields: BuildStockPlacementCancelConfirmParams["fields"];
}

/** Параметры хука {@link useStockPlacementCancelConfirm}. */
export interface UseStockPlacementCancelConfirmParams {
    /** Действие отмены размещения после подтверждения (обычно `mutation.mutateAsync`). */
    perform: (variables: StockPlacementCancelPlacementVariables) => Promise<unknown>;
}

/**
 * Подтверждение и запуск отмены размещения через app confirm.
 * Сначала загружает движение для preview, затем показывает confirm с DetailList.
 */
export function useStockPlacementCancelConfirm({ perform }: UseStockPlacementCancelConfirmParams) {
    const confirmAndCancel = useCallback(
        async (params: ConfirmStockPlacementCancelParams): Promise<void> => {
            const movement = await storePlacesServices.loadLatestPlacementMovement({
                lotCode: params.lotCode,
                cellCode: params.cellCode,
            });

            if (movement == null) {
                notification.warning("Движение размещения не найдено для отмены.");
                return;
            }

            const accepted = await confirm.show(
                buildStockPlacementCancelConfirmOptions({
                    movement,
                    fields: params.fields,
                }),
            );

            if (!accepted) {
                return;
            }

            await perform({
                lotCode: params.lotCode,
                cellCode: params.cellCode,
                contentCode: params.contentCode,
            });
        },
        [perform],
    );

    return { confirmAndCancel };
}
